import React, { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheckIcon, BellIcon, BriefcaseBusinessIcon, CompassIcon, CrownIcon, FileBadgeIcon, GraduationCapIcon, HouseIcon, LayoutDashboardIcon, MessageCircleIcon, MoreHorizontalIcon, SettingsIcon, SparklesIcon, StickyNoteIcon, StoreIcon, SunIcon, UserCircleIcon, WalletIcon, XIcon, BoxIcon, ZapIcon } from "lucide-react";
import { Logo } from "./Logo";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";
import { ConsoleSwitcher } from "./ConsoleSwitcher";
import { AssistantDock } from "../intelligence/AssistantDock";
import { QuickCapture } from "../notes/QuickCapture";
import { DataModeBanner } from "../platform/DataModeBanner";
import { useKeyboardOpen } from "../../hooks/useKeyboardOpen";
type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  end?: boolean;
};

/**
 * Sidebar navigation, grouped by the platform's own architecture rather than
 * presented as one flat list. Every entry is a real destination — nothing here
 * leads to a page that does not exist.
 */
const GROUPS: {
  label: string;
  items: NavItem[];
}[] = [{
  label: 'Main',
  items: [{
    to: '/',
    label: 'Home',
    icon: LayoutDashboardIcon,
    end: true
  }, {
    to: '/academics',
    label: 'Academics',
    icon: GraduationCapIcon
  }, {
    to: '/housing',
    label: 'Housing',
    icon: HouseIcon
  }, {
    to: '/market',
    label: 'Marketplace',
    icon: StoreIcon
  }, {
    to: '/social',
    label: 'Campus',
    icon: SparklesIcon
  }, {
    to: '/power',
    label: 'Power',
    icon: ZapIcon
  }, {
    to: '/money',
    label: 'Money',
    icon: WalletIcon
  }]
}, {
  label: 'Communication',
  items: [{
    to: '/community',
    label: 'Messages',
    icon: MessageCircleIcon
  }, {
    to: '/notifications',
    label: 'Notifications',
    icon: BellIcon
  }]
}, {
  label: 'Productivity',
  items: [{
    to: '/notes',
    label: 'Notes',
    icon: StickyNoteIcon
  }, {
    to: '/services',
    label: 'Campus services',
    icon: FileBadgeIcon
  }, {
    to: '/discover',
    label: 'Discover',
    icon: CompassIcon
  }, {
    to: '/opportunities',
    label: 'Opportunities',
    icon: BriefcaseBusinessIcon
  }, {
    to: '/marketplace',
    label: 'Knowledge & orders',
    icon: StoreIcon
  }]
}, {
  label: 'You',
  items: [{
    to: '/profile',
    label: 'Profile',
    icon: UserCircleIcon
  }, {
    to: '/identity',
    label: 'Academic identity',
    icon: BadgeCheckIcon
  }]
}, {
  label: 'System',
  items: [{
    to: '/settings',
    label: 'Settings',
    icon: SettingsIcon
  }, {
    to: '/support',
    label: 'Help & support',
    icon: BoxIcon
  }]
}];

/**
 * Five bottom-bar destinations, chosen by what a student opens the phone for:
 * their day, what is happening around them, buying something, replying to
 * someone, and everything else.
 */
const MOBILE_PRIMARY: NavItem[] = [{
  to: '/',
  label: 'Home',
  icon: LayoutDashboardIcon,
  end: true
}, {
  to: '/social',
  label: 'Campus',
  icon: SparklesIcon
}, {
  to: '/market',
  label: 'Market',
  icon: StoreIcon
}, {
  to: '/community',
  label: 'Messages',
  icon: MessageCircleIcon
}];
const navItemClass = ({
  isActive
}: {
  isActive: boolean;
}) => `flex min-h-touch items-center gap-3 rounded-xl px-3 text-body-sm transition-colors duration-150 ease-out ${isActive ? 'bg-brand-50 font-semibold text-brand-800' : 'font-medium text-ink-muted hover:bg-canvas hover:text-ink'}`;
function SidebarNav({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  return <nav aria-label="Primary" className="flex flex-col gap-5 px-3 pb-6" data-id="element-5318" data-mp="3693ltw4d0579">
      {GROUPS.map((group, index) => <div key={group.label} className={index > 0 ? 'border-t border-line pt-5' : undefined} data-id="element-5319" data-mp="29rqud82qs8w1" data-mpi={index}>
          {index > 0 && <h2 className="mb-2 px-3 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-5320" data-mp="2drhjz8gj8iyz">
              {group.label}
            </h2>}
          <ul className="space-y-1" data-id="element-5321" data-mp="1ull6l7xc6u8d">
            {group.items.map(item => {
          const Icon = item.icon;
          return <li key={item.to} data-id="element-5322" data-mp="24dh4ahvigic2">
                  <NavLink to={item.to} end={item.end} onClick={onNavigate} className={navItemClass} data-id="element-5323" data-mp="24eirfkpswva5">
                    {({
                isActive
              }) => <>
                        <Icon aria-hidden className={`h-5 w-5 shrink-0 ${isActive ? 'text-brand-700' : 'text-ink-subtle'}`} data-id="element-5324" data-mp="2ddmbaqwslepp" />
                        <span className="truncate" data-id="element-5325" data-mp="28esv2qq4f9lu">{item.label}</span>
                      </>}
                  </NavLink>
                </li>;
        })}
          </ul>
        </div>)}
    </nav>;
}

/** Appearance is honest about what exists: light only, for now. */
function AppearanceControl() {
  return <div className="px-6" data-id="element-5326" data-mp="szdb3dwqyy82">
      <p className="flex min-h-touch items-center gap-2 rounded-xl border border-line px-3 text-body-sm font-medium text-ink" data-id="element-5327" data-mp="t0odb9ajnxx2">
        <SunIcon aria-hidden className="h-4 w-4 text-ink-subtle" data-id="element-5328" data-mp="2zn2te85i7i29" />
        Light
        <span className="ml-auto text-caption font-normal text-ink-subtle" data-id="element-5329" data-mp="2zp6vqlnr890f">Only theme</span>
      </p>
    </div>;
}
function PlusCard() {
  return <div className="px-6 pb-6 pt-4" data-id="element-5330" data-mp="2pxsykkj9zlag">
      <div className="rounded-card border border-line bg-brand-50 px-4 py-4" data-id="element-5331" data-mp="2pwrdrocgjkfj">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white" data-id="element-5332" data-mp="2uut5exny8s2m">
          <CrownIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-5333" data-mp="2gv9xo5z1viqu" />
        </span>
        <p className="mt-3 text-body-sm font-semibold text-ink" data-id="element-5334" data-mp="2kyg5hi1t9hzf">Upgrade your experience</p>
        <p className="mt-1 text-caption leading-5 text-ink-muted" data-id="element-5335" data-mp="26yotfmuuubo2">
          Unlock more with Campuzon Plus.
        </p>
        <Link to="/#campuzon-plus" className="mt-3 inline-flex min-h-touch w-full items-center justify-center rounded-lg bg-brand-700 px-3 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 hover:text-white" data-id="element-5336" data-mp="2byb7fyiibvxx">
          Explore plans
        </Link>
      </div>
    </div>;
}
export function StudentShell() {
  const [moreOpen, setMoreOpen] = useState(false);
  /* The bottom bar is anchored to the layout viewport, so while the keyboard is
     open it would sit on top of the field being typed into. It steps aside. */
  const keyboardOpen = useKeyboardOpen();
  return <div className="min-h-full w-full bg-canvas" data-id="element-5337" data-mp="3py4uztl98xxz">
      {/* Fixed application sidebar. Desktop only; the sheet carries it below lg. */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col overflow-y-auto border-r border-line bg-surface lg:flex" data-id="element-5338" data-mp="rh204cluqwf0">
        <div className="flex h-16 shrink-0 items-center px-6" data-id="element-5339" data-mp="howctg6jp365">
          <Logo data-id="element-5340" data-mp="hn1mtngf2kv7" />
        </div>
        <div className="flex-1" data-id="element-5341" data-mp="cqutcu57j1u5">
          <SidebarNav data-id="element-5342" data-mp="cps1ok028vd6" />
        </div>
        <div className="shrink-0 border-t border-line pt-4" data-id="element-5343" data-mp="cogv6cf8d9id">
          <AppearanceControl data-id="element-5344" data-mp="1a79y6gap2woc" />
          <PlusCard data-id="element-5345" data-mp="1a7jracr1djy9" />
        </div>
      </aside>

      <div className="flex min-h-full flex-col lg:pl-[260px]" data-id="element-5346" data-mp="15dxmt7v10mdj">
        <DataModeBanner data-id="element-5347" data-mp="1dnmneee0ir7c" />

        <header className="sticky top-0 z-20 border-b border-line bg-surface" data-id="element-5348" data-mp="1doejdqbiuqy5">
          <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-3 px-4 sm:px-6" data-id="element-5349" data-mp="1hoybgfh8n4df">
            <div className="lg:hidden" data-id="element-5350" data-mp="1moktd280h8sk">
              <Logo data-id="element-5351" data-mp="1mmq4jctmcwj7" />
            </div>
            <div className="hidden min-w-0 flex-1 md:flex" data-id="element-5352" data-mp="1rne5aq7ngrqm">
              <GlobalSearch data-id="element-5353" data-mp="1roz10j5pagq2" />
            </div>
            <div className="ml-auto flex items-center gap-2" data-id="element-5354" data-mp="1wmqqz7msbsrm">
              <div className="md:hidden" data-id="element-5355" data-mp="1wm8nbpxsmc9o">
                <GlobalSearch data-id="element-5356" data-mp="20nu43mdg7ap5" />
              </div>
              <QuickCapture data-id="element-5357" data-mp="20m9ca4iiq5uz" />
              <AssistantDock data-id="element-5358" data-mp="25esoax1k4sxo" />
              <NotificationBell data-id="element-5359" data-mp="25eiz3boc6pt1" />
              <ConsoleSwitcher current="student" data-id="element-5360" data-mp="25fc5gbjdvdan" />
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 pb-24 pt-6 sm:px-6 lg:pb-12" data-id="element-5361" data-mp="2ad3yx9zof7gy">
          <Outlet data-id="element-5362" data-mp="2feji0wian3wp" />
        </main>
      </div>

      <nav aria-label="Primary mobile" hidden={keyboardOpen} className={`fixed bottom-0 left-0 right-0 z-30 flex border-t border-line bg-surface lg:hidden ${keyboardOpen ? 'hidden' : ''}`} data-id="element-5363" data-mp="2kbtgf1nnupwo">
        {MOBILE_PRIMARY.map(item => {
        const Icon = item.icon;
        return <NavLink key={item.to} to={item.to} end={item.end} className={({
          isActive
        }) => `flex min-h-touch-lg min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-caption transition-colors duration-150 ease-out ${isActive ? 'font-semibold text-brand-700' : 'font-medium text-ink-muted'}`} data-id="element-5364" data-mp="30q19tjwjn8eb">
              <Icon aria-hidden className="h-5 w-5 shrink-0" data-id="element-5365" data-mp="2hxp7uy9sy94p" />
              <span className="w-full truncate text-center" data-id="element-5366" data-mp="2hvck24hd54jq">{item.label}</span>
            </NavLink>;
      })}
        <button type="button" onClick={() => setMoreOpen(true)} aria-label="More sections" className="cz-block flex min-h-touch-lg min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-caption font-medium text-ink-muted" data-id="element-5367" data-mp="2vqopb5t5adew">
          <MoreHorizontalIcon aria-hidden className="h-5 w-5 shrink-0" data-id="element-5368" data-mp="1ya84ng7cp2mi" />
          <span className="w-full truncate text-center" data-id="element-5369" data-mp="drfhu5wv5w2v">More</span>
        </button>
      </nav>

      <AnimatePresence data-id="element-5370" data-mp="ii467cd8ycxs">
        {moreOpen && <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center lg:hidden" data-id="element-5371" data-mp="ij5uwjn6qxxz">
            <motion.div className="absolute inset-0 bg-ink/40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setMoreOpen(false)} data-id="element-5372" data-mp="4rsdbb8u8a5l" />
            <motion.div role="dialog" aria-label="All sections" className="relative max-h-[86dvh] w-full overflow-y-auto rounded-t-2xl border-t border-line bg-surface pb-8 sm:max-w-lg sm:rounded-card sm:border" initial={{
          y: 24,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 16,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-5373" data-mp="11foxsr7ebpm3">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-5 py-3.5" data-id="element-5374" data-mp="9p18m53rpv85">
                <p className="text-body font-semibold text-ink" data-id="element-5375" data-mp="encigfu2c5qd">All sections</p>
                <button type="button" onClick={() => setMoreOpen(false)} aria-label="Close" className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md text-ink-muted hover:bg-canvas hover:text-ink" data-id="element-5376" data-mp="j7fmrktm3z">
                  <XIcon aria-hidden className="h-5 w-5" data-id="element-5377" data-mp="3n3o1g91ngrns" />
                </button>
              </div>
              <div className="pt-4" data-id="element-5378" data-mp="3r6u3f4hqbwog">
                <SidebarNav onNavigate={() => setMoreOpen(false)} data-id="element-5379" data-mp="3r3pu5speqaqx" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}